import { useClerk } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { Navigate } from "react-router";

import { LoaderTeamz } from "@/components/ui/loader-teamz";
import { useTRPC } from "@/lib/trpc";
import { routes } from "@/routes";

import { UserContext } from "./user-context";
import { getUserContextRecovery } from "./user-context-error";

export function UserContextWrapper({ children }: { children: React.ReactNode }) {
  const trpc = useTRPC();

  const { data: user, status: getMeStatus, error: getMeError } = useQuery(trpc.user.getMe.queryOptions());
  const { data: streamUser, status: getStreamUserStatus, error: getStreamUserError } = useQuery(trpc.message.getStreamChatUser.queryOptions());

  if (getMeStatus === "pending" || getStreamUserStatus === "pending") {
    return (
      <div className="h-dvh w-full">
        <LoaderTeamz />
      </div>
    );
  }

  if (getMeStatus === "error") {
    const recovery = getUserContextRecovery(getMeError);
    if (recovery === "sign-out") {
      return <SignOutAfterAuthFailure />;
    }
    if (recovery === "onboarding") {
      return <Navigate replace to={routes.onboarding} />;
    }
    throw new Error(`Failed to fetch user: ${getMeError.message}`, { cause: getMeError });
  }

  if (getStreamUserStatus === "error") {
    if (getUserContextRecovery(getStreamUserError) === "sign-out") {
      return <SignOutAfterAuthFailure />;
    }
    throw new Error(`Failed to fetch stream user: ${getStreamUserError.message}`, { cause: getStreamUserError });
  }

  return (
    // eslint-disable-next-line react/no-unstable-context-value
    <UserContext.Provider value={{ user, streamUser }}>
      {children}
    </UserContext.Provider>
  );
}

function SignOutAfterAuthFailure() {
  const { signOut } = useClerk();

  useEffect(() => {
    void signOut({ redirectUrl: routes.signin }).catch(() => {
      window.location.assign(routes.signin);
    });
  }, [signOut]);

  return (
    <div className="h-dvh w-full">
      <LoaderTeamz />
    </div>
  );
}
